import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, PhoneIcon, MapPinIcon, SendIcon, CalendarIcon, MonitorPlayIcon, CheckIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { DemoModal } from '../components/DemoModal';
import { PageHero } from '../components/PageHero';
const contactDetails = [{
  icon: PhoneIcon,
  label: 'Phone',
  value: '+1 (555) 123-4567'
}, {
  icon: MailIcon,
  label: 'Email',
  value: 'hello@roicomplete.com'
}, {
  icon: MapPinIcon,
  label: 'Office Location',
  value: '1200 Market Street, Suite 400, Atlanta, GA'
}];
export function ContactPage() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
  };
  return <motion.main initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.4
  }} className="min-h-screen w-full bg-stark-white overflow-x-hidden" data-id="element-233" data-mp="15g8d348z9ary">
      <Header onOpenDemo={() => setIsDemoModalOpen(true)} data-id="element-234" data-mp="1e8imwkfyne8o" />

      <PageHero eyebrow="Contact" title="Let's talk about your numbers." description="Schedule a consultation, request a demo, or send us a note — a specialist will respond within one business day." data-id="element-235" data-mp="1j73i25gcv7q9" />

      <section className="py-16 md:py-24" data-id="element-236" data-mp="qig12m2n34ii">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6" data-id="element-237" data-mp="vhit95xitg92">
          {[{
          icon: CalendarIcon,
          title: 'Schedule a Consultation',
          body: 'Pick a date and time that works and meet with an operations specialist.',
          action: 'Choose a time'
        }, {
          icon: MonitorPlayIcon,
          title: 'Request a Demo',
          body: 'See dashboards, reporting, and AI insights applied to your business.',
          action: 'Request a demo'
        }].map((item, idx) => <motion.div key={item.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: idx * 0.1
        }} className="p-8 md:p-10 bg-cool-gray rounded-2xl border border-midnight-navy/10 space-y-5" data-id="element-238" data-mp="1w62ud8ue13x7" data-mpi={idx}>
              <div className="w-12 h-12 rounded-2xl bg-royal-blue text-stark-white flex items-center justify-center" data-id="element-239" data-mp="18edv18j5ff6r">
                <item.icon className="w-6 h-6" data-id="element-240" data-mp="ko9x9hsn69no" />
              </div>
              <h2 className="text-midnight-navy text-2xl font-montserrat font-extrabold" data-id="element-241" data-mp="fqi0oah1xzd5">
                {item.title}
              </h2>
              <p className="text-midnight-navy/60 leading-relaxed" data-id="element-242" data-mp="2rctoidnqr0fq">
                {item.body}
              </p>
              <button onClick={() => setIsDemoModalOpen(true)} className="inline-flex items-center gap-2 text-royal-blue font-bold hover:gap-3 transition-all" data-id="element-243" data-mp="2hgqobdcg9bvf">
                {item.action}
                <SendIcon className="w-4 h-4" data-id="element-244" data-mp="3a6fr9w8pzlzo" />
              </button>
            </motion.div>)}
        </div>
      </section>

      <section className="pb-24 md:pb-32" data-id="element-245" data-mp="3f24s02wh78xk">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-12" data-id="element-246" data-mp="3157ujiluqt7p">
          <div className="space-y-10" data-id="element-247" data-mp="355rq4hqs1orq">
            <div className="space-y-6" data-id="element-248" data-mp="354qeoc5un09h">
              {contactDetails.map((item, idx) => <motion.div key={item.label} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: idx * 0.08
            }} className="flex items-start gap-4" data-id="element-249" data-mp="1po80oijuw6bk" data-mpi={idx}>
                  <div className="w-11 h-11 bg-midnight-navy text-stark-white rounded-xl flex items-center justify-center shrink-0" data-id="element-250" data-mp="2swmvxi29kys0">
                    <item.icon className="w-5 h-5" data-id="element-251" data-mp="2nytq4q4vi6s9" />
                  </div>
                  <div data-id="element-252" data-mp="2iy61gb44a217">
                    <div className="text-xs font-bold uppercase tracking-widest text-midnight-navy/40 mb-1" data-id="element-253" data-mp="2ixe691efm6bg">
                      {item.label}
                    </div>
                    <div className="text-midnight-navy font-semibold leading-snug" data-id="element-254" data-mp="17k1zahndl7l2">
                      {item.value}
                    </div>
                  </div>
                </motion.div>)}
            </div>

            <div className="rounded-2xl overflow-hidden border border-midnight-navy/10 shadow-lg" data-id="element-255" data-mp="3f6ljmmjj2sp5">
              <iframe title="ROI Complete office location map" src="https://www.openstreetmap.org/export/embed.html?bbox=-84.42%2C33.74%2C-84.36%2C33.78&layer=mapnik" className="w-full h-64 border-0" loading="lazy" data-id="element-256" data-mp="3j7p2lhwvh4ul" />
            </div>
          </div>

          <div className="lg:col-span-2" data-id="element-257" data-mp="1si6rtrqkakw">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-cool-gray p-8 md:p-12 rounded-2xl border border-midnight-navy/10" data-id="element-258" data-mp="6oz51gsng5bp">
              {!isSubmitted ? <form onSubmit={handleSubmit} className="space-y-6" data-id="element-259" data-mp="3krnzikez8txi">
                  <h2 className="text-midnight-navy text-2xl font-montserrat font-extrabold" data-id="element-260" data-mp="3ftcq2aslglft">
                    Send us a message
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6" data-id="element-261" data-mp="3ttxbzftt2xdj">
                    <div className="space-y-2" data-id="element-262" data-mp="3otjc4l2cytql">
                      <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40 ml-1" data-id="element-263" data-mp="3ospz52ct3800">
                        Full Name
                      </label>
                      <input required type="text" className="w-full px-6 py-4 bg-stark-white border border-midnight-navy/10 rounded-lg focus:outline-none focus:border-royal-blue transition-colors" placeholder="Jane Doe" data-id="element-264" data-mp="2m1j41tv8rs1" />
                    </div>
                    <div className="space-y-2" data-id="element-265" data-mp="ldklq7jza3tj">
                      <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40 ml-1" data-id="element-266" data-mp="levp469ih9k4">
                        Email Address
                      </label>
                      <input required type="email" className="w-full px-6 py-4 bg-stark-white border border-midnight-navy/10 rounded-lg focus:outline-none focus:border-royal-blue transition-colors" placeholder="jane@example.com" data-id="element-267" data-mp="19x5u4g81j550" />
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-268" data-mp="r0eceb85cy32">
                    <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40 ml-1" data-id="element-269" data-mp="r15zsyrlbpi9">
                      Company
                    </label>
                    <input required type="text" className="w-full px-6 py-4 bg-stark-white border border-midnight-navy/10 rounded-lg focus:outline-none focus:border-royal-blue transition-colors" placeholder="Your business name" data-id="element-270" data-mp="ha1j9um9nwlb" />
                  </div>
                  <div className="space-y-2" data-id="element-271" data-mp="ir8mn9d3xxk4">
                    <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40 ml-1" data-id="element-272" data-mp="isjnp1f64r7j">
                      Message
                    </label>
                    <textarea required rows={6} className="w-full px-6 py-4 bg-stark-white border border-midnight-navy/10 rounded-lg focus:outline-none focus:border-royal-blue transition-colors resize-none" placeholder="Tell us about your business and what you're trying to solve..." data-id="element-273" data-mp="8skfj5mnv2v7" />
                  </div>
                  <button type="submit" className="w-full md:w-auto bg-royal-blue text-stark-white px-12 py-5 rounded-full font-bold hover:bg-royal-blue-dark transition-all shadow-xl shadow-royal-blue/20 flex items-center justify-center gap-3 group" data-id="element-274" data-mp="3m9tklmidp2ze">
                    Send Message
                    <SendIcon className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" data-id="element-275" data-mp="1wrd1lqggmk4j" />
                  </button>
                </form> : <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="py-20 text-center space-y-6" data-id="element-276" data-mp="21omxnoyctu1c">
                  <div className="w-20 h-20 bg-royal-blue/10 text-royal-blue rounded-full flex items-center justify-center mx-auto" data-id="element-277" data-mp="1izorfspru5q7">
                    <CheckIcon className="w-10 h-10" data-id="element-278" data-mp="1ns9i4ldds2pi" />
                  </div>
                  <div className="space-y-2" data-id="element-279" data-mp="qa9jjbteksyi">
                    <h3 className="text-3xl font-montserrat font-extrabold text-midnight-navy" data-id="element-280" data-mp="q9ghgo5e2rmp">
                      Message Sent
                    </h3>
                    <p className="text-midnight-navy/60 text-lg" data-id="element-281" data-mp="owsre2wpuv6u">
                      Thank you for reaching out. We'll be in touch within one
                      business day.
                    </p>
                  </div>
                </motion.div>}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer data-id="element-282" data-mp="13siigfgrdyif" />
      <DemoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} data-id="element-283" data-mp="6749badyr98w" />
    </motion.main>;
}